///
/// EarthBrowser
///
/// Hurricane Data Provider
///
/// Provides active tropical cyclone data from the local sync database with
/// custom rendering for the hurricane browser window
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { DataRow, IDataProvider, DataQuery, QueryResult, ColumnMetadata, DataStats, FilterGroup, ProviderCapabilities } from './types'
import { parseTrack, stormCategoryLabel, stormSource, type StormProperties } from '@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable'
import { basinName } from '@/earthbrowser/dataset-distribution/hurricanes/StormFeature'
import { cStormCategoryHex, stormCategoryHex } from '@/earthbrowser/dataset-distribution/hurricanes/stormDisplay'
import { stormTrend, type StormTrend } from '@/earthbrowser/dataset-distribution/hurricanes/stormInsights'
import { formatDistanceToNow } from 'date-fns'

function categoryHex(storm: StormProperties): string {
	return stormCategoryHex(storm.category, storm.max_wind_kt)
}

const cTrendGlyph: Record<StormTrend, { glyph: string, hex: string, title: string }> = {
	intensifying: { glyph: "↑", hex: "#ff8f20", title: "Intensifying" },
	weakening: { glyph: "↓", hex: "#6fd8a8", title: "Weakening" },
	steady: { glyph: "→", hex: "#94a3b8", title: "Steady" },
}

/** Arrow showing the wind trend over the trailing observations */
function TrendIndicator({ storm }: { storm: StormProperties }) {
	const trend = stormTrend(parseTrack(storm.observed_track))
	if (!trend) return null
	const style = cTrendGlyph[trend]
	return (
		<span className="ml-1 font-bold cursor-help" style={{ color: style.hex }} title={style.title}>
			{style.glyph}
		</span>
	)
}

// Thumbnail canvas in SVG user units
const cThumbWidth = 64
const cThumbHeight = 32
const cThumbPad = 3
const cThumbMaxPoints = 32

/** Tiny track + cone preview: dim history, bright forecast, cone outline */
function TrackThumbnail({ storm }: { storm: StormProperties }) {
	const observed = decimate(parseTrack(storm.observed_track), cThumbMaxPoints)
	const forecast = decimate(parseTrack(storm.forecast_track), cThumbMaxPoints)
	const cone = coneOuterRing(storm.cone)

	const all: Array<[number, number]> = [
		...observed.map(p => [p.lon, p.lat] as [number, number]),
		...forecast.map(p => [p.lon, p.lat] as [number, number]),
		...cone,
	]
	if (all.length < 2) return <span className="text-white/40">—</span>

	let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity
	for (const [lon, lat] of all) {
		if (lon < minLon) minLon = lon
		if (lon > maxLon) maxLon = lon
		if (lat < minLat) minLat = lat
		if (lat > maxLat) maxLat = lat
	}
	// Uniform scale so the shape isn't distorted, roughly correcting the
	// lon/lat aspect at this latitude
	const midLat = (minLat + maxLat) / 2
	const lonScale = Math.max(Math.cos(midLat * Math.PI / 180), 0.2)
	const spanX = Math.max((maxLon - minLon) * lonScale, 0.1)
	const spanY = Math.max(maxLat - minLat, 0.1)
	const scale = Math.min((cThumbWidth - 2 * cThumbPad) / spanX, (cThumbHeight - 2 * cThumbPad) / spanY)
	const offsetX = (cThumbWidth - spanX * scale) / 2
	const offsetY = (cThumbHeight - spanY * scale) / 2

	const x = (lon: number) => offsetX + (lon - minLon) * lonScale * scale
	const y = (lat: number) => cThumbHeight - offsetY - (lat - minLat) * scale
	const toPath = (points: Array<[number, number]>, close = false) =>
		points.map(([lon, lat], i) => `${i === 0 ? "M" : "L"}${x(lon).toFixed(1)},${y(lat).toFixed(1)}`).join(" ") + (close ? " Z" : "")

	const hex = categoryHex(storm)

	return (
		<svg width={cThumbWidth} height={cThumbHeight} viewBox={`0 0 ${cThumbWidth} ${cThumbHeight}`} className="block">
			{cone.length >= 3 && (
				<path d={toPath(cone, true)} fill="#ffffff10" stroke="#ffffff35" strokeWidth="0.75" />
			)}
			{observed.length >= 2 && (
				<path d={toPath(observed.map(p => [p.lon, p.lat]))} fill="none" stroke="#ffffff55" strokeWidth="1" />
			)}
			{forecast.length >= 2 && (
				<path d={toPath(forecast.map(p => [p.lon, p.lat]))} fill="none" stroke={hex} strokeWidth="1.25" strokeDasharray="2 1.5" />
			)}
			<circle cx={x(storm.lon)} cy={y(storm.lat)} r="2" fill={hex} />
		</svg>
	)
}

function decimate<T>(points: T[], maxPoints: number): T[] {
	if (points.length <= maxPoints) return points
	const step = points.length / maxPoints
	const out: T[] = []
	for (let i = 0; i < maxPoints; i++) {
		out.push(points[Math.floor(i * step)])
	}
	out.push(points[points.length - 1])
	return out
}

/** Outer ring of the first polygon of the cone GeoJSON, decimated */
function coneOuterRing(coneJson: string | null): Array<[number, number]> {
	if (!coneJson) return []
	try {
		const geometry = JSON.parse(coneJson) as { type: string, coordinates: unknown }
		const ring = geometry.type === "Polygon"
			? (geometry.coordinates as number[][][])[0]
			: geometry.type === "MultiPolygon"
				? (geometry.coordinates as number[][][][])[0]?.[0]
				: null
		if (!ring || ring.length < 4) return []
		return decimate(ring, 48).map(([lon, lat]) => [lon, lat] as [number, number])
	} catch {
		return []
	}
}

export class HurricaneDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: () => Promise<StormProperties[]>
	private cachedData: StormProperties[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: () => Promise<StormProperties[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		this.cachedData = await this.queryFn()
		this.isInitializedFlag = true
	}

	async refreshData(): Promise<void> {
		this.cachedData = await this.queryFn()
	}

	/**
	 * Update cached data directly (for when data is managed externally)
	 */
	updateCachedData(data: StormProperties[]): void {
		this.cachedData = data
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'name',
			name: 'Storm',
			priority: 1,
			minWidth: 120,
			type: 'string',
			nullable: false,
			render: (value: unknown, row?: DataRow) => {
				const storm = row as unknown as StormProperties | undefined
				return (
					<div className="flex items-center gap-2 min-w-0">
						<span
							className="flex-shrink-0 w-2 h-2 rounded-full"
							style={{ backgroundColor: storm ? categoryHex(storm) : cStormCategoryHex[0] }}
						/>
						<span className="font-medium truncate">{value as string}</span>
					</div>
				)
			}
		},
		{
			id: 'observed_track',
			name: 'Track',
			priority: 3,
			minWidth: 90,
			type: 'string',
			nullable: false,
			render: (_value: unknown, row?: DataRow) => {
				const storm = row as unknown as StormProperties | undefined
				if (!storm) return <span className="text-white/40">—</span>
				return <TrackThumbnail storm={storm} />
			}
		},
		{
			id: 'category',
			name: 'Intensity',
			priority: 1,
			minWidth: 110,
			type: 'number',
			nullable: false,
			render: (_value: unknown, row?: DataRow) => {
				const storm = row as unknown as StormProperties | undefined
				if (!storm) return <span className="text-white/40">—</span>
				return (
					<span className="font-semibold" style={{ color: categoryHex(storm) }}>
						{stormCategoryLabel(storm)}
						<TrendIndicator storm={storm} />
					</span>
				)
			}
		},
		{
			id: 'basin',
			name: 'Basin',
			priority: 4,
			minWidth: 160,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<span className="text-white/80 truncate">{basinName(String(value))}</span>
			)
		},
		{
			id: 'max_wind_kt',
			name: 'Winds',
			priority: 2,
			minWidth: 90,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const wind = value as number | null
				if (wind == null) return <span className="text-white/40">—</span>
				return <span className="text-white/80">{Math.round(wind)} kt</span>
			}
		},
		{
			id: 'mslp_mb',
			name: 'Pressure',
			priority: 5,
			minWidth: 100,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const mslp = value as number | null
				if (mslp == null) return <span className="text-white/40">—</span>
				return <span className="text-white/80">{Math.round(mslp)} mb</span>
			}
		},
		{
			id: 'advisory_date',
			name: 'Advisory',
			priority: 4,
			minWidth: 200,
			type: 'date',
			nullable: false,
			render: (value: unknown, row?: DataRow) => {
				const storm = row as unknown as StormProperties | undefined
				const date = new Date(value as string)
				const exactTime = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
				return (
					<span className="text-white/80 cursor-help" title={exactTime}>
						{formatDistanceToNow(date, { addSuffix: true })}
						{storm && <span className="text-white/40 text-xs"> &bull; {stormSource(storm.basin)}</span>}
					</span>
				)
			}
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
			memorySize: JSON.stringify(this.cachedData).length,
			lastModified: new Date()
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let filteredData = [...this.cachedData]

		// Apply full-text search
		if (query.search?.query) {
			const lowerSearch = query.search.query.toLowerCase()
			filteredData = filteredData.filter((row) => {
				return [row.name, row.basin, basinName(row.basin), row.classification].some((value) => {
					if (value == null) return false
					return String(value).toLowerCase().includes(lowerSearch)
				})
			})
		}

		// Apply sorting
		if (query.sort && query.sort.length > 0) {
			const sortSpec = query.sort[0]
			const sortBy = sortSpec.column
			const sortDirection = sortSpec.direction

			filteredData.sort((a, b) => {
				const aVal = a[sortBy as keyof StormProperties]
				const bVal = b[sortBy as keyof StormProperties]

				if (aVal == null && bVal == null) return 0
				if (aVal == null) return 1
				if (bVal == null) return -1

				let comparison = 0
				if (typeof aVal === 'string' && typeof bVal === 'string') {
					comparison = aVal.localeCompare(bVal)
				} else if (typeof aVal === 'number' && typeof bVal === 'number') {
					comparison = aVal - bVal
				} else {
					comparison = String(aVal).localeCompare(String(bVal))
				}

				return sortDirection === 'desc' ? -comparison : comparison
			})
		}

		// Calculate pagination
		const totalCount = filteredData.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const rows = filteredData.slice(offset, offset + limit)

		return {
			rows: rows as unknown as DataRow[],
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime: 0,
			fromCache: true
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		return this.cachedData.length
	}

	async dispose(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}
